<template>
  <FishScene cena="abissal" :escurecimento="0.35" class="melhorias">
    <div class="melhorias__interior">
      <h1 class="t-titulo-tela melhorias__titulo">{{ $t('upgrade.title') }}</h1>

      <!-- D11 (1.3): a roda e as árvores são a mesma pergunta — "no que gasto o que ganhei". Duas
           abas, nenhum item novo no rodapé. -->
      <FishTabs v-model="aba" :abas="abas" :rotulo="$t('upgrade.title')" class="melhorias__abas" />

      <ArvoreDeHabilidades v-if="aba === 'habilidades'" />

      <div v-else-if="isLoading" class="melhorias__carregando">
        <FishSkeleton forma="bloco" altura="380px" />
      </div>

      <!--
        7.2 — A RODA (D19). Oito atributos num octógono: o nó mostra ícone, anel e nível — e SÓ
        isso. A tela anterior era a pior aplicação do problema do D6: oito cards, cada um com
        rótulo, descrição, efeito atual, próximo nível, acordeão de custos, preço e botão. Oito
        paredes empilhadas, nada com destaque porque tudo tinha.

        O anel aceso = "o ouro cobre o próximo nível". O centro é o ouro, porque ele é a restrição
        comum de toda melhoria. O painel de detalhe descreve UM atributo por vez.
      -->
      <FishNodeMap
        v-else
        v-model="atributoAtivo"
        :nos="nos"
        arranjo="ciclico"
        :progresso="progresso"
        :lado="440"
        :rotulo="$t('upgrade.title')"
        class="melhorias__roda"
      >
        <template #centro>
          <div class="melhorias__ouro">
            <FishIcon nome="coins" tamanho="medio" />
            <span class="t-numero melhorias__ouro-valor">{{ formatar(playerGold) }}</span>
          </div>
        </template>

        <template #detalhe="{ no }">
          <FishFrame v-if="no" class="melhorias__detalhe" :titulo="no.rotulo">
            <p class="t-corpo melhorias__descricao">{{ $t(`upgrade.stats.${no.id}.description`) }}</p>

            <dl class="melhorias__efeitos">
              <div class="melhorias__efeito">
                <dt class="t-legenda">{{ $t('upgrade.currentEffect') }}</dt>
                <dd class="t-numero">{{ efeitoDe(no.id, no.grau) }}</dd>
              </div>
              <FishIcon nome="chevron-right" tamanho="pequeno" class="melhorias__seta" />
              <div class="melhorias__efeito">
                <dt class="t-legenda">{{ $t('upgrade.nextEffect') }}</dt>
                <dd class="t-numero melhorias__efeito-proximo">{{ efeitoDe(no.id, no.grau + 1) }}</dd>
              </div>
            </dl>

            <div class="acao-principal melhorias__acao">
              <FishButton
                variante="primario"
                tamanho="grande"
                bloco
                som="compra"
                :desabilitado="!no.aceso"
                :motivo="no.aceso ? '' : motivoDoBloqueio(no)"
                :carregando="melhorando === no.id"
                @click="melhorar(no.id)"
              >
                {{ $t('upgrade.upgrade') }} · {{ formatar(no.custo) }}
                <FishIcon nome="coins" tamanho="miudo" />
              </FishButton>
            </div>
          </FishFrame>
        </template>
      </FishNodeMap>

      <p v-if="error" class="melhorias__erro">{{ error }}</p>
    </div>

    <UpgradeSuccessModal
      v-if="showSuccessModal"
      :stat="lastUpgradedStat"
      @close="showSuccessModal = false"
    />
  </FishScene>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import UpgradeSuccessModal from '@/shared/upgrade/UpgradeSuccessModal.vue';
import ArvoreDeHabilidades from '@/shared/upgrade/ArvoreDeHabilidades.vue';
import { FishScene, FishNodeMap, FishFrame, FishButton, FishIcon, FishSkeleton, FishTabs } from '@/shared/ui';

/**
 * 7.2 — as Melhorias viram a RODA (D19, UI-19, D6).
 *
 * ## O que saiu
 *
 * A parede de oito `UpgradeCard` — e junto dela um fundo de "constelações" desenhado em DOM com
 * `getBoundingClientRect` por estrela e listeners de resize que nunca eram removidos.
 *
 * ## Os ícones
 *
 * Os oito atributos usavam emoji do sistema (`⚔️ 🎯 ✨ ⚡ ❤️ 🧠 🗣️ 🍀`) — ícones de navegador, sem
 * nada a ver com pesca (UI-10). Agora são os do pacote (D20): o arpão é a puxada, a rede pega
 * mais, a concha fechada é a sorte.
 *
 * ## Os efeitos por nível
 *
 * `efeitoDe()` espelha as fórmulas do backend (as mesmas que o `UpgradeCard` mostrava). Se uma
 * fórmula mudar em `packages/domain`, este é o único lugar do cliente a atualizar — e a
 * divergência de `luck` (7,5 contra 4,5) que motivou a regra "fórmula mora no domain" é o aviso
 * de sempre conferir lá primeiro.
 */
const ATRIBUTOS = [
  { id: 'fishingPower', icone: 'atributo-poder' },
  { id: 'catchRate', icone: 'atributo-captura' },
  { id: 'criticalRate', icone: 'atributo-critico' },
  { id: 'reelSpeed', icone: 'atributo-recolhimento' },
  { id: 'currentStamina', icone: 'atributo-energia' },
  { id: 'intelligence', icone: 'atributo-inteligencia' },
  { id: 'charisma', icone: 'atributo-carisma' },
  { id: 'luck', icone: 'atributo-sorte' },
];

export default {
  name: 'UpgradePage',
  components: {
    UpgradeSuccessModal,
    ArvoreDeHabilidades,
    FishScene,
    FishNodeMap,
    FishFrame,
    FishButton,
    FishIcon,
    FishSkeleton,
    FishTabs,
  },
  data() {
    return {
      aba: 'atributos',
      atributoAtivo: 'fishingPower',
      showSuccessModal: false,
      lastUpgradedStat: null,
      isLoading: true,
      error: null,
      melhorando: null,
    };
  },
  computed: {
    ...mapState({
      playerGold: (state) => state.player.gold,
    }),
    ...mapGetters('upgrade', ['getStatLevel', 'getStatCost']),
    abas() {
      return [
        { id: 'atributos', rotulo: this.$t('skills.tabs.attributes'), icone: 'nav-melhorias' },
        { id: 'habilidades', rotulo: this.$t('skills.tabs.skills'), icone: 'star' },
      ];
    },
    nos() {
      return ATRIBUTOS.map(({ id, icone }) => {
        const grau = this.getStatLevel(id);
        const custo = this.getStatCost(id);
        return {
          id,
          icone,
          grau,
          custo,
          rotulo: this.$t(`upgrade.stats.${id}.title`),
          aceso: this.playerGold >= custo,
        };
      });
    },
    /**
     * A trilha enche com o nível total (D19-2). A régua é 8×50 — o nível em que a tela antiga
     * carimbava "Rare" —, então a roda fecha o anel no meio do jogo e fica cheia dali em diante:
     * uma trilha que só encheria no nível 200×8 nunca sairia do zero visual.
     */
    progresso() {
      const total = ATRIBUTOS.reduce((soma, a) => soma + this.getStatLevel(a.id), 0);
      return Math.min(1, total / 400);
    },
  },
  methods: {
    formatar(n) {
      return Number(n || 0).toLocaleString('pt-BR');
    },
    motivoDoBloqueio(no) {
      return this.$t('shop.panel.needGold', {
        amount: (no.custo - this.playerGold).toLocaleString('pt-BR'),
      });
    },
    /** As fórmulas de efeito por nível, espelhadas do backend (ver o comentário do topo). */
    efeitoDe(stat, level) {
      switch (stat) {
        case 'fishingPower': return `+${(level * 0.5).toFixed(1)}`;
        // 0,5 ponto percentual por nível — EFEITO_POR_PONTO.catchRate no domínio. A tela
        // dizia 1 por nível e prometia o dobro do que o servidor aplica (28/09/2026).
        case 'catchRate': return `+${(level * 0.5).toFixed(1)}%`;
        case 'criticalRate': return `+${(level * 0.5).toFixed(1)}%`;
        case 'reelSpeed': return `+${(level * 0.1).toFixed(1)}`;
        case 'currentStamina': return `+${level * 2}`;
        case 'intelligence': return `+${level}% XP`;
        case 'charisma': return `+${level}%`;
        case 'luck': return `+${level * 0.5}%`;
        default: return `+${level}`;
      }
    },
    async melhorar(stat) {
      this.melhorando = stat;
      try {
        const result = await this.$store.dispatch('upgrade/upgradeStat', stat);
        if (result && result.success) {
          this.lastUpgradedStat = stat;
          this.showSuccessModal = true;
        }
      } catch (error) {
        this.error = error.message || this.$t('upgrade.title');
        setTimeout(() => { this.error = null; }, 3000);
      } finally {
        this.melhorando = null;
      }
    },
  },
  async mounted() {
    try {
      await this.$store.dispatch('upgrade/fetchUpgradeStats');
    } catch (error) {
      this.error = error.message || 'Failed to fetch upgrade stats';
    } finally {
      this.isLoading = false;
    }
  },
};
</script>

<style scoped>
.melhorias {
  min-height: 100vh;
}

.melhorias__interior {
  max-width: 980px;
  margin: 0 auto;
  /* A folha ativa do rodapé tem 99px e a faixa com o nome sobe mais ~26: com os 64px de antes o
   * botão "Melhorar" e o preço ficavam atrás do rótulo "Melhorias" no telefone (relato de 23/09). */
  padding: var(--e-5) var(--e-4) calc(var(--altura-barra) + var(--e-5));
}

/* A roda é a tela: no desktop ela centra no que sobra entre as vigas. No telefone NÃO — a
 * centralização empurrava o título para baixo do cabeçalho fixo (visto a 360px). */
@media (min-width: 900px) {
  .melhorias__interior {
    min-height: calc(100vh - 160px);
    display: flex;
    flex-direction: column;
    justify-content: center;
  }
}

.melhorias__roda {
  align-items: center;
}

.melhorias__titulo {
  text-align: center;
  color: var(--papel-luz);
  text-shadow: 2px 2px 0 var(--agua-abissal), -2px 2px 0 var(--agua-abissal),
    2px -2px 0 var(--agua-abissal), -2px -2px 0 var(--agua-abissal);
  margin-bottom: var(--e-5);
}

/* A 360px o f-6 quebra "Melhorias de Pescador" em duas linhas; uma linha em f-4 devolve a tela
 * para a roda, que é quem manda aqui. */
@media (max-width: 599px) {
  .melhorias__titulo {
    font-size: var(--f-4);
  }
}

.melhorias__abas {
  align-self: center;
  margin-bottom: var(--e-4);
}

.melhorias__carregando {
  max-width: 380px;
  margin: 0 auto;
}

/* O centro da roda: o ouro, a restrição comum (D19). */
.melhorias__ouro {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--e-1);
  color: var(--ouro-claro);
  text-shadow: 1px 1px 0 var(--agua-abissal);
}

.melhorias__ouro-valor {
  font-size: var(--f-4);
}

.melhorias__descricao {
  color: var(--tinta-fraca);
  margin: 0 0 var(--e-3);
}

.melhorias__efeitos {
  display: flex;
  align-items: center;
  gap: var(--e-3);
  margin: 0 0 var(--e-4);
}

.melhorias__efeito {
  flex: 1;
}

.melhorias__efeito dt {
  color: var(--tinta-fraca);
}

.melhorias__efeito dd {
  margin: 0;
  font-size: var(--f-5);
  color: var(--tinta);
}

.melhorias__efeito-proximo {
  color: var(--cor-bom);
}

.melhorias__seta {
  flex-shrink: 0;
  opacity: 0.6;
}

.melhorias__acao {
  background: none;
  padding: 0;
}

.melhorias__erro {
  margin: var(--e-3) auto 0;
  max-width: 420px;
  background: var(--papel);
  color: var(--cor-ruim);
  font-family: var(--fonte-corpo);
  text-align: center;
  padding: var(--e-2);
  border: 2px solid var(--cor-ruim);
}
</style>
